import { UserAvatar } from "@/components/user-avatar";
import { ImageLightbox } from "@/components/image-lightbox";
import { useEffect, useMemo, useRef, useState } from "react";
import { logListingEvent } from "@/lib/stats";
import { recommend, rememberViewed, similarAds } from "@/lib/recommendations";
import { VerifiedBadge } from "@/components/verified-badge";
import { RecommendedAds } from "@/components/recommended-ads";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import {
  Eye,
  MapPin,
  MessageCircle,
  Phone,
  Clock,
  Crown,
  Sparkles,
  Zap,
  ShieldAlert,
  Send,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { AdCard } from "@/components/ad-card";
import { ShareBar } from "@/components/share-bar";
import { ReportDialog } from "@/components/report-dialog";

import { ads, categoryName, formatPrice, subcategoriesOf } from "@/lib/data";
import { TimeAgo } from "@/components/time-ago";
import { fetchActiveListings, fetchListingById, incrementViews, isUuid } from "@/lib/listings";
import { specEntries } from "@/lib/specs";
import { PromoteDialog } from "@/components/promote-dialog";
import { imageSrcSet, optimizedImage } from "@/lib/img";
import { SITE_URL, adImageAlt, adSlug, idFromSlug } from "@/lib/seo";
import { SEO_DEFAULTS, fetchSeoSettings, renderSeo } from "@/lib/seo-settings";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { deleteListing } from "@/lib/admin";
import { azError } from "@/lib/az-errors";
import { createPromoRequest, type PromoPlanKind } from "@/lib/promo-requests";
import { fetchPublicProfile, profileName, profileSlug } from "@/lib/profiles";
import { fetchSellerRating } from "@/lib/stores";

export const Route = createFileRoute("/elan/$id")({
  loader: async ({ params }) => {
    const realId = idFromSlug(params.id);
    const ad = isUuid(realId)
      ? await fetchListingById(realId)
      : (ads.find((a) => a.id === realId) ?? null);
    if (!ad) throw notFound();
    const rating = isUuid(realId) && ad.ownerId ? await fetchSellerRating(ad.ownerId) : null;
    const reviewAuthors = rating
      ? await Promise.all(
          rating.top.map(async (r) => {
            const p = await fetchPublicProfile(r.author_id).catch(() => null);
            return p ? profileName(p) : "Albazar istifadəçisi";
          }),
        )
      : [];
    return { ad, seo: await fetchSeoSettings(), rating, reviewAuthors };
  },
  errorComponent: () => (
    <div className="container-x py-24 text-center">
      <h1 className="text-2xl font-extrabold">Elan yüklənmədi</h1>
      <p className="mt-2 text-sm text-muted-foreground">Yenidən cəhd edin.</p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-x py-24 text-center">
      <h1 className="text-2xl font-extrabold">Elan tapılmadı</h1>
      <Link to="/elanlar" search={{ q: "", kateqoriya: "", seher: "", sirala: "yeni" }}>
        <Button className="mt-5 rounded-full">Bütün elanlar</Button>
      </Link>
    </div>
  ),
  head: ({ loaderData }) => {
    if (!loaderData)
      return {
        meta: [{ title: "Elan tapılmadı — Albazar.az" }, { name: "robots", content: "noindex" }],
      };
    const { ad } = loaderData;
    const url = `${SITE_URL}/elan/${adSlug(ad)}`;
    const cat = categoryName(ad.category);
    const seo = loaderData.seo ?? SEO_DEFAULTS;
    const vars = {
      basliq: ad.title,
      qiymet: formatPrice(ad.price),
      seher: ad.city,
      kateqoriya: cat,
    };
    const title = renderSeo(seo["listing_title_template"] || SEO_DEFAULTS["listing_title_template"]!, vars);
    const description = renderSeo(
      seo["listing_description_template"] || SEO_DEFAULTS["listing_description_template"]!,
      vars,
    );
    const keywords = [renderSeo(seo["listing_keywords_template"] || "", vars), seo["global_keywords"] ?? ""]
      .filter(Boolean)
      .join(", ");
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: keywords },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: ad.images[0]! },
        { property: "og:image:alt", content: adImageAlt(ad) },
        { property: "og:site_name", content: "Albazar.az" },
        { property: "og:locale", content: "az_AZ" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ad.images[0]! },
        { property: "og:url", content: url },
        { property: "og:type", content: "product" },
        { property: "product:price:amount", content: String(ad.price) },
        { property: "product:price:currency", content: "AZN" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: ad.title,
            description: ad.description || description,
            image: ad.images,
            category: cat,
            url,
            brand: { "@type": "Brand", name: "Albazar.az" },
            offers: {
              "@type": "Offer",
              price: ad.price,
              priceCurrency: "AZN",
              availability: "https://schema.org/InStock",
              itemCondition: "https://schema.org/UsedCondition",
              priceValidUntil: new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10),
              areaServed: ad.city,
              url,
            },
            ...(loaderData.rating
              ? {
                  aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: loaderData.rating.average,
                    bestRating: 5,
                    worstRating: 1,
                    reviewCount: loaderData.rating.count,
                  },
                  ...(loaderData.rating.top.length
                    ? {
                        review: loaderData.rating.top.map((r, i) => ({
                          "@type": "Review",
                          author: {
                            "@type": "Person",
                            name: loaderData.reviewAuthors[i] ?? "Albazar istifadəçisi",
                          },
                          datePublished: r.created_at.slice(0, 10),
                          reviewBody: r.comment ?? "",
                          reviewRating: {
                            "@type": "Rating",
                            ratingValue: r.rating,
                            bestRating: 5,
                            worstRating: 1,
                          },
                        })),
                      }
                    : {}),
                }
              : {}),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ana səhifə", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Elanlar", item: `${SITE_URL}/elanlar` },
              {
                "@type": "ListItem",
                position: 3,
                name: cat,
                item: `${SITE_URL}/kateqoriya/${ad.category}`,
              },
              { "@type": "ListItem", position: 4, name: ad.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AdDetail,
});


const QUICK_MESSAGES = [
  "Hələ satışdadır?",
  "Endirim edərsiniz?",
  "Harada baxa bilərəm?",
  "Barter edərsiniz?",
];

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/50 py-2 last:border-b-0">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="text-right text-[13px] font-bold text-foreground">{value}</span>
    </div>
  );
}


function AdDetail() {
  const { ad } = Route.useLoaderData();
  const { data: sellerProfile } = useQuery({
    queryKey: ["public-profile", ad.ownerId],
    queryFn: () => fetchPublicProfile(ad.ownerId!),
    enabled: Boolean(ad.ownerId),
  });
  const sellerName = sellerProfile ? profileName(sellerProfile) : ad.seller;
  const { user } = useAuth();
  const isOwner = Boolean(user?.id && ad.ownerId && user.id === ad.ownerId);
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const goTo = (i: number) => {
    const next = Math.max(0, Math.min(i, ad.images.length - 1));
    setActive(next);
    const el = trackRef.current;
    if (el) el.scrollTo({ left: next * el.clientWidth, behavior: "auto" });
  };


  const [viewerOpen, setViewerOpen] = useState(false);
  const viewerRef = useRef(false);
  useEffect(() => {
    viewerRef.current = viewerOpen;
  }, [viewerOpen]);
  const [message, setMessage] = useState("");

  const [hydrated, setHydrated] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [promo, setPromo] = useState<{ plan: "vip" | "premium"; days: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  useEffect(() => setHydrated(true), []);

  const shortId = (() => {
    let h = 0;
    for (const ch of String(ad.id)) h = (h * 31 + ch.charCodeAt(0)) % 1000000;
    return String(h).padStart(6, "0");
  })();

  useEffect(() => {
    void incrementViews(ad.id);
    void logListingEvent(String(ad.id), "view", ad.ownerId ?? null);
    rememberViewed(ad);
  }, [ad.id]);

  const track = (kind: "phone" | "whatsapp" | "message" | "share") =>
    void logListingEvent(String(ad.id), kind, ad.ownerId ?? null);

  const { data: liveAds } = useQuery({
    queryKey: ["listings", "active"],
    queryFn: fetchActiveListings,
    staleTime: 60_000,
  });
  const allAds = useMemo(() => [...(liveAds ?? []), ...ads], [liveAds]);
  const similar = useMemo(() => similarAds(allAds, ad, 8), [allAds, ad]);
  const recommended = useMemo(
    () =>
      hydrated
        ? recommend(allAds, {
            exclude: [String(ad.id), ...similar.map((s) => String(s.id))],
            limit: 4,
          })
        : [],
    [allAds, ad.id, similar, hydrated],
  );
  const specs = specEntries(ad.category, ad.specs);
  const subName = subcategoriesOf(ad.category).find((s) => s.slug === ad.specs?.["alt"])?.name;

  const cards: { label: string; value: string }[] = [
    { label: "Şəhər", value: ad.city },
    { label: "Kateqoriya", value: categoryName(ad.category) },
    ...(subName ? [{ label: "Alt kateqoriya", value: subName }] : []),
    ...specs.map((s) => ({ label: s.label, value: s.value })),
    { label: "Razılaşma", value: ad.negotiable ? "Bəli" : "Xeyr" },
  ];

  const longText = (ad.description ?? "").length > 260;


  const requestPromo = async (plan: PromoPlanKind, days: number) => {
    if (!user?.id) {
      toast.error("Zəhmət olmasa hesabınıza daxil olun");
      return;
    }
    try {
      const res = await createPromoRequest({ listingId: ad.id, userId: user.id, plan, days });
      if (res === "exists") {
        toast.info("Bu elan üçün müraciətiniz artıq baxılır");
        return;
      }
      toast.success("Müraciətiniz admin təsdiqinə göndərildi");
    } catch (e) {
      toast.error(azError(e));
    }
  };


  const sendMessage = (text: string) => {
    if (!text.trim()) {
      toast.error("Mesaj boş ola bilməz");
      return;
    }
    setMessage("");
    toast.success("Mesaj göndərildi");
  };

  return (
    <div className="container-x max-w-3xl pb-32 pt-4">
      <nav className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">
          Ana səhifə
        </Link>{" "}
        /{" "}
        <Link
          to="/kateqoriya/$slug"
          params={{ slug: ad.category }}
          search={{ alt: "", marka: "" }}
          className="hover:text-primary"
        >
          {categoryName(ad.category)}
        </Link>
      </nav>

      {/* Elan sahibi üçün irəliləmə düymələri */}
      {isOwner && (
        <section className="mt-3 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => void requestPromo("bump", 1)}
            className="flex flex-col items-start gap-1 rounded-2xl border border-border bg-card p-3 text-left shadow-card transition-transform active:scale-[0.98]"
          >
            <span className="grid size-8 place-items-center rounded-xl bg-info/15 text-info">
              <Zap className="size-4" />
            </span>
            <span className="text-[13px] font-bold leading-tight">İrəli çək</span>
            <span className="text-[11px] leading-tight text-muted-foreground">Admin təsdiqi ilə</span>
          </button>
          <button
            type="button"
            onClick={() => void requestPromo("vip", 7)}
            className="flex flex-col items-start gap-1 rounded-2xl border border-border bg-card p-3 text-left shadow-card transition-transform active:scale-[0.98]"
          >
            <span className="grid size-8 place-items-center rounded-xl bg-vip/20 text-vip">
              <Crown className="size-4" />
            </span>
            <span className="text-[13px] font-bold leading-tight">VIP et</span>
            <span className="text-[11px] leading-tight text-muted-foreground">Admin təsdiqi ilə</span>
          </button>
          <button
            type="button"
            onClick={() => void requestPromo("premium", 7)}
            className="flex flex-col items-start gap-1 rounded-2xl border border-border bg-card p-3 text-left shadow-card transition-transform active:scale-[0.98]"
          >
            <span className="grid size-8 place-items-center rounded-xl bg-premium/20 text-premium">
              <Sparkles className="size-4" />
            </span>
            <span className="text-[13px] font-bold leading-tight">Premium et</span>
            <span className="text-[11px] leading-tight text-muted-foreground">Admin təsdiqi ilə</span>
          </button>
        </section>
      )}

      {/* Sahibkar: redaktə və silmə */}
      {isOwner && (
        <section className="mt-2 grid grid-cols-2 gap-2">
          <Link to="/elan-redakte/$id" params={{ id: ad.id }} className="block">
            <Button variant="outline" className="h-11 w-full rounded-2xl text-sm font-bold">
              <Pencil className="size-4" /> Redaktə et
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            className="h-11 w-full rounded-2xl border-destructive/40 text-sm font-bold text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="size-4" /> Sil
          </Button>
        </section>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elanı silmək istəyirsiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              “{ad.title}” elanı həmişəlik silinəcək. Bu əməliyyatı geri qaytarmaq mümkün deyil.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">İmtina</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={async (e) => {
                e.preventDefault();
                setDeleting(true);
                try {
                  await deleteListing(ad.id);
                  toast.success("Elan silindi");
                  setConfirmDelete(false);
                  void navigate({ to: "/profil" });
                } catch (err) {
                  toast.error(azError(err, "Elan silinmədi"));
                } finally {
                  setDeleting(false);
                }
              }}
              className="rounded-2xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Bəli, sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {ad.videoUrl && (
        <section className="mt-3">
          <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-card">
            <video
              src={ad.videoUrl}
              poster={ad.images[0]}
              controls
              playsInline
              preload="none"
              controlsList="nodownload"
              className="aspect-video w-full bg-black object-contain"
            />
          </div>
        </section>
      )}

      {/* Şəkil qalereyası */}
      <section className="mt-3 -mx-4 sm:mx-0">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-card">
          <div
            ref={trackRef}
            onScroll={(e) => {
              if (viewerRef.current) return;
              const el = e.currentTarget;
              const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
              if (i !== active) setActive(Math.min(i, ad.images.length - 1));
            }}
            className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {ad.images.map((src: string, i: number) => (
              <button
                key={`slide-${i}`}
                type="button"
                onClick={() => setViewerOpen(true)}
                aria-label={`Şəkil ${i + 1} — tam ekranda aç`}
                className="w-full shrink-0 cursor-zoom-in snap-center"
              >
                <img
                  src={optimizedImage(src, 800)}
                  srcSet={imageSrcSet(src, 800)}
                  alt={adImageAlt(ad, i)}
                  width={800}
                  height={640}
                  decoding="async"
                  fetchPriority={i === 0 ? "high" : "low"}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="aspect-[5/4] w-full bg-muted object-contain"
                />
              </button>
            ))}
          </div>


          <div className="pointer-events-none absolute left-3 top-3 flex gap-2">
            {ad.badge === "vip" && (
              <Badge className="rounded-full bg-vip text-vip-foreground shadow-lg">
                <Crown className="mr-1 size-3" /> VIP
              </Badge>
            )}
            {ad.badge === "premium" && (
              <Badge className="rounded-full bg-premium text-premium-foreground shadow-lg">
                <Sparkles className="mr-1 size-3" /> Premium
              </Badge>
            )}
          </div>

          {ad.images.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Əvvəlki şəkil"
                onClick={() => goTo(active - 1)}
                className="absolute left-2 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-full bg-background/85 backdrop-blur"
              >
                <ChevronLeft className="size-5" />
              </button>
              <button
                type="button"
                aria-label="Növbəti şəkil"
                onClick={() => goTo(active + 1)}
                className="absolute right-2 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-full bg-background/85 backdrop-blur"
              >
                <ChevronRight className="size-5" />
              </button>

              <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs font-bold text-white">
                {active + 1} / {ad.images.length}
              </span>
            </>
          )}

        </div>

        {ad.images.length > 1 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {ad.images.map((src: string, i: number) => (
              <button
                key={`thumb-${i}`}
                type="button"
                aria-label={`Şəkil ${i + 1}`}
                onClick={() => goTo(i)}
                className={`shrink-0 overflow-hidden rounded-xl border-2 transition ${
                  i === active ? "border-info" : "border-border/60 opacity-70"
                }`}
              >
                <img
                  src={optimizedImage(src, 160, 55)}
                  alt=""
                  width={80}
                  height={64}
                  decoding="async"
                  loading="lazy"
                  className="h-16 w-20 bg-muted object-cover"
                />
              </button>
            ))}

          </div>
        )}

        <ShareBar ad={ad} className="mt-3" />


        {viewerOpen && (
          <ImageLightbox
            images={ad.images}
            index={active}
            onIndexChange={(i: number) => {
              setActive(i);
              goTo(i);
            }}
            onClose={() => setViewerOpen(false)}
            alt={adImageAlt(ad)}
          />
        )}
      </section>


      {/* Qiymət + başlıq */}
      <section className="mt-4 rounded-3xl border border-border bg-card p-5 shadow-card">
        <p className="text-[34px] font-black leading-none tracking-tight text-foreground">
          {formatPrice(ad.price)}
        </p>
        {ad.negotiable && (
          <p className="mt-1 text-xs text-muted-foreground">Razılaşma yolu ilə</p>
        )}
        <h1 className="mt-2 text-lg font-bold leading-snug">{ad.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <MapPin className="size-3.5" /> {ad.city}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="size-3.5" /> <TimeAgo iso={ad.createdAt} />
          </span>
          <span className="flex items-center gap-1">
            <Eye className="size-3.5" /> {ad.views}
          </span>
        </div>



        <PromoteDialog
          adId={ad.id}
          open={promo !== null}
          plan={promo?.plan ?? "vip"}
          days={promo?.days ?? 7}
          onOpenChange={(o: boolean) => {
            if (!o) setPromo(null);
          }}
        />
      </section>

      {/* Xüsusiyyətlər */}
      <section className="mt-4 rounded-3xl border border-border bg-card p-5 shadow-card">
        <h2 className="text-base font-bold tracking-tight">Xüsusiyyətlər</h2>
        <div className="mt-2 grid gap-x-6 sm:grid-cols-2">
          {cards.map((c) => (
            <SpecRow key={c.label} label={c.label} value={c.value} />
          ))}
        </div>
      </section>


      {/* Təsvir */}
      <section className="mt-4 rounded-3xl border border-border bg-card p-5 shadow-card">
        <h2 className="text-base font-bold tracking-tight">Təsvir</h2>
        <p
          className={`mt-2 whitespace-pre-line text-[15px] leading-7 text-muted-foreground transition-all duration-300 ${
            expanded || !longText ? "" : "line-clamp-4"
          }`}
        >
          {ad.description}
        </p>
        {longText && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 flex items-center gap-1 text-sm font-bold text-info"
          >
            {expanded ? "Yığışdır" : "Davamını oxu"}
            <ChevronDown
              className={`size-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </section>

      {/* Xəritə */}
      <section className="mt-4 overflow-hidden rounded-3xl border border-border bg-card shadow-card">
        <div className="flex items-center justify-between gap-3 p-5 pb-3">
          <h2 className="text-base font-bold tracking-tight">Yerləşmə</h2>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${ad.city}, Azərbaycan`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm font-bold text-info"
          >
            <MapPin className="size-4" /> Xəritədə aç
          </a>
        </div>
        <iframe
          title={`${ad.city} xəritədə`}
          src={`https://maps.google.com/maps?q=${encodeURIComponent(`${ad.city}, Azərbaycan`)}&z=12&output=embed`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="h-56 w-full border-0"
        />
      </section>

      {/* Satıcı */}
      <section className="mt-4 rounded-3xl border border-border bg-card p-5 shadow-card">
        {ad.ownerId ? (
          <Link
            to="/istifadeci/$id"
            params={{ id: profileSlug({ id: ad.ownerId, full_name: sellerName }) }}
            className="-m-2 flex items-center gap-3 rounded-2xl p-2 transition-colors active:scale-[0.99] hover:bg-accent"
          >
            <UserAvatar src={sellerProfile?.avatar_url} name={sellerName} className="size-12" online={false} />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1 truncate font-bold">
                {sellerName}
                <VerifiedBadge verified={sellerProfile?.verified} compact />
              </p>
              <p className="text-xs text-muted-foreground">Profilə bax</p>
            </div>
            <ChevronRight className="size-4 text-muted-foreground" />
          </Link>
        ) : (
          <div className="flex items-center gap-3">
            <UserAvatar name={ad.seller} className="size-12" online={false} />
            <div className="min-w-0">
              <p className="truncate font-bold">{ad.seller}</p>
              <p className="text-xs text-muted-foreground">
                {ad.sellerSince} ildən bəri Albazar.az-da
              </p>
            </div>
          </div>
        )}
        {ad.ownerId ? (
          <Link
            to="/istifadeci/$id"
            params={{ id: profileSlug({ id: ad.ownerId, full_name: sellerName }) }}
            className="mt-3 flex min-h-12 items-center justify-between rounded-2xl border border-border px-4 py-3 text-sm font-semibold transition-colors hover:bg-accent"
          >
            İstifadəçinin bütün elanları
            <ChevronRight className="size-4 text-muted-foreground" />
          </Link>
        ) : (
          <Link
            to="/elanlar"
            search={{ q: ad.seller, kateqoriya: "", seher: "", sirala: "yeni" }}
            className="mt-3 flex min-h-12 items-center justify-between rounded-2xl border border-border px-4 py-3 text-sm font-semibold transition-colors hover:bg-accent"
          >
            İstifadəçinin bütün elanları
            <ChevronRight className="size-4 text-muted-foreground" />
          </Link>
        )}

        {hydrated && ad.phone ? (
          showPhone ? (
            <a href={`tel:${ad.phone.replace(/\s/g, "")}`} className="mt-3 block">
              <Button className="h-12 w-full rounded-2xl bg-success text-base text-success-foreground hover:bg-success/90">
                <Phone className="size-4" /> {ad.phone}
              </Button>
            </a>
          ) : (
            <Button
              type="button"
              onClick={() => {
                setShowPhone(true);
                track("phone");
              }}
              className="mt-3 h-12 w-full rounded-2xl text-base shadow-glow"
            >
              <Phone className="size-4" /> Nömrəni göstər
            </Button>
          )
        ) : (
          <Link to="/giris" className="mt-3 block">
            <Button className="h-12 w-full rounded-2xl text-base shadow-glow">
              <Phone className="size-4" /> Nömrəni görmək üçün daxil ol
            </Button>
          </Link>
        )}
      </section>

      {/* Təhlükəsizlik */}
      <section className="mt-4 rounded-3xl border border-vip/40 bg-vip/10 p-5">
        <h2 className="flex items-center gap-2 text-sm font-bold">
          <ShieldAlert className="size-5 text-vip" /> Təhlükəsizlik xəbərdarlığı
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Malı görmədən qabaqcadan ödəniş etməyin. Görüşü ictimai yerdə təyin edin, kart
          məlumatlarınızı və SMS kodlarınızı heç kimlə paylaşmayın.
        </p>
      </section>

      {/* Hazır mesajlar */}
      <section className="mt-4 rounded-3xl border border-border bg-card p-5 shadow-card">
        <h2 className="text-base font-bold tracking-tight">Satıcıya mesaj yaz</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {QUICK_MESSAGES.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => sendMessage(q)}
              className="rounded-full border border-border bg-muted/50 px-3.5 py-2 text-xs font-semibold transition-all hover:-translate-y-0.5 hover:border-info hover:text-info"
            >
              {q}
            </button>
          ))}
        </div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Salam, elan aktualdır?"
          className="mt-3 min-h-24 rounded-2xl"
        />
        <Button className="mt-3 w-full rounded-2xl" onClick={() => sendMessage(message)}>
          <Send className="size-4" /> Göndər
        </Button>
      </section>

      {/* Elan məlumatları */}
      <section className="mt-4 rounded-3xl border border-border bg-card p-5 text-sm shadow-card">
        <div className="flex justify-between border-b border-border/60 py-2">
          <span className="text-muted-foreground">Elan nömrəsi</span>
          <span className="font-semibold">#{shortId}</span>
        </div>
        <div className="flex justify-between border-b border-border/60 py-2">
          <span className="text-muted-foreground">Baxış sayı</span>
          <span className="font-semibold">{ad.views}</span>
        </div>
        <div className="flex justify-between py-2">
          <span className="text-muted-foreground">Yenilənmə tarixi</span>
          <span className="font-semibold">
            {new Date(ad.createdAt).toISOString().slice(0, 10).split("-").reverse().join(".")}
          </span>
        </div>
        <ReportDialog listingId={isUuid(ad.id) ? ad.id : null} reportedUserId={ad.ownerId ?? null} />
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-extrabold tracking-tight">Oxşar elanlar</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {similar.map((a, i) => (
            <AdCard key={a.id} ad={a} index={i} />
          ))}
        </div>
      </section>

      <RecommendedAds ads={recommended} subtitle="Baxdığınız elanlara əsasən seçildi" />

      {/* Sticky alt panel */}
      <div className="fixed inset-x-0 bottom-16 z-40 border-t border-border bg-background/95 p-3 backdrop-blur md:bottom-0">
        <div className="container-x flex max-w-3xl gap-3">
          <a
            href={ad.phone ? `tel:${ad.phone.replace(/\s/g, "")}` : "#"}
            onClick={(e) => {
              if (!ad.phone) {
                e.preventDefault();
                toast.error("Nömrəni görmək üçün daxil olun");
                return;
              }
              track("phone");
            }}
            className="flex-1"
          >
            <Button className="h-13 w-full rounded-2xl bg-success text-base font-bold text-success-foreground shadow-lg transition-transform hover:scale-[1.02] hover:bg-success/90">
              <Phone className="size-5" /> Zəng et
            </Button>
          </a>
          <Link
            to="/mesajlar"
            search={{ user: ad.ownerId ?? "" }}
            onClick={() => track("message")}
            className="flex-1"
          >
            <Button className="h-13 w-full rounded-2xl bg-info text-base font-bold text-white shadow-lg transition-transform hover:scale-[1.02] hover:bg-info/90">
              <MessageCircle className="size-5" /> Mesaj yaz
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
